/**
 * ScamBlocker AI — Korunan Üyeler (/members)
 * Mobil uygulama koltukları: ekle (user_id) / çıkar, koltuk sayacı.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import styles from '@/features/_shared/page.module.css';
import apiClient from '@/shared/lib/api/client';
import { API } from '@/shared/lib/api/endpoints';

interface Member {
  id: string;
  user_id: string;
  role: string;
  user_role?: string;
  created_at: string;
}

interface MembersResp {
  members: Member[];
  seats?: number;
  used: number;
}

export function MembersPage() {
  const qc = useQueryClient();
  const [userId, setUserId] = useState('');

  const { data, isLoading, isError } = useQuery<MembersResp>({
    queryKey: ['members'],
    queryFn: async () => (await apiClient.get(API.org.members)).data,
  });

  const addMut = useMutation({
    mutationFn: async () => (await apiClient.post(API.org.members, { user_id: userId })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['members'] });
      setUserId('');
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      window.alert(e.response?.data?.error ?? 'Üye eklenemedi'),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => (await apiClient.delete(API.org.memberById(id))).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
  });

  const list = data?.members ?? [];
  const seatLabel = data
    ? `${data.used}${data.seats != null ? ` / ${data.seats}` : ''} koltuk`
    : '';

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Users size={22} className={styles.titleIcon} />
        <h1 className={styles.title}>Korunan Üyeler</h1>
        {data && <span className={styles.countPill}>{seatLabel}</span>}
      </div>

      <div className={styles.formRow}>
        <input
          className={styles.input}
          placeholder="Kullanıcı ID (user_id)"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          style={{ minWidth: 280 }}
        />
        <button
          type="button"
          className={styles.actionBtn}
          disabled={!userId || addMut.isPending}
          onClick={() => addMut.mutate()}
        >
          <UserPlus size={14} /> Üye Ekle
        </button>
      </div>

      <div className={styles.tableWrapper}>
        {isLoading && (
          <div className={styles.center}>
            <span className={styles.spinner} />
          </div>
        )}
        {isError && <div className={styles.errorBox}>Üyeler yüklenemedi.</div>}
        {!isLoading && !isError && (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Kullanıcı ID</th>
                <th>Org Rolü</th>
                <th>Kullanıcı Rolü</th>
                <th>Eklenme</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {list.map((m) => (
                <tr key={m.id} className={styles.row}>
                  <td className={styles.mono}>{m.user_id}</td>
                  <td>{m.role}</td>
                  <td>{m.user_role ?? '—'}</td>
                  <td className={styles.mono}>
                    {new Date(m.created_at).toLocaleDateString('tr-TR')}
                  </td>
                  <td>
                    <button
                      type="button"
                      className={`${styles.actionBtn} ${styles.dangerBtn}`}
                      onClick={() => window.confirm('Üye çıkarılsın mı?') && delMut.mutate(m.id)}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && (
                <tr>
                  <td colSpan={5} className={styles.empty}>
                    Üye yok.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
